<template>
    <div ref="imageGallery" class="relative -mr-2 -ml-2 md:mx-0">
        <!-- Single Gallery Wrapper (Mobile + Desktop) -->
        <div class="flex justify-center gap-4 lg:gap-8">
            <!-- Thumbnail Column (Hidden on mobile, visible on desktop) -->
            <div
                ref="thumbnailColumn"
                class="hidden md:flex md:flex-col w-[96px] flex-shrink-0 relative"
                :style="{ height: thumbnailColumnHeight > 0 ? thumbnailColumnHeight + 'px' : '0' }"
            >
                <template v-if="thumbnailColumnHeight > 0">
                    <!-- Thumbnail container (full height) -->
                    <div
                        ref="thumbnailContainer"
                        class="flex flex-col gap-2 overflow-hidden h-full"
                    >
                        <div
                            class="flex flex-col gap-2 transition-transform duration-300"
                            :style="{ transform: `translateY(-${thumbnailScrollOffset}px)` }"
                        >
                            <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
                            <div
                                v-for="(image, index) in media"
                                :key="index"
                                role="button"
                                class="w-full flex justify-center items-center relative cursor-pointer bg-base-200 p-2 rounded transition-all duration-200"
                                :class="{
                                    'shadow-[inset_0_0_0_2px] shadow-primary/30': index === activeImageIndex
                                }"
                                tabindex="0"
                                @mouseenter="setActiveImage(index)"
                                @focus="setActiveImage(index)"
                                @click="setActiveImage(index)"
                            >
                                <MiscImage
                                    :src="image?.media?.url"
                                    :alt="image?.media?.alt"
                                    :quality="50"
                                    width="80"
                                    height="108"
                                    fit="fit"
                                    background="ffffff"
                                    :modifiers="{ extend: 1, trim: '1:ffffff:1' }"
                                    lazy-load
                                    class="no-forced-colors:mix-blend-multiply"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Absolutely positioned navigation arrows -->
                    <button
                        v-if="canScrollUp"
                        class="absolute top-[-2px] left-0 right-0 flex items-center justify-center h-8 text-neutral bg-base-100/90 backdrop-blur-sm z-10 cursor-pointer"
                        aria-label="Scroll thumbnails up"
                        @click="scrollThumbnailUp"
                    >
                        <MiscIcon name="chevron-up" class="w-4 h-4" />
                    </button>

                    <button
                        v-if="canScrollDown"
                        class="absolute bottom-[-2px] left-0 right-0 flex items-center justify-center h-8 text-neutral bg-base-100/90 backdrop-blur-sm z-10 cursor-pointer"
                        aria-label="Scroll thumbnails down"
                        @click="scrollThumbnailDown"
                    >
                        <MiscIcon name="chevron-down" class="w-4 h-4" />
                    </button>
                </template>
            </div>
            
            <!-- Main Image Area (Unified wrapper for mobile + desktop) -->
            <div
                ref="mainImageContainer"
                class="flex-1 relative w-full overflow-x-auto md:overflow-hidden flex flex-row flex-nowrap md:gap-0 md:flex-col md:max-h-[690px]"
                :class="{
                    'gap-2': media && media?.length > 1
                }"
            >
                <!-- Render all images for mobile horizontal scroll, show only active for desktop -->
                <div
                    v-for="(image, index) in media"
                    :key="index"
                    class="relative flex items-center justify-center min-w-[85vw] bg-base-200 md:rounded md:min-w-full md:max-w-[546px]"
                    :class="{
                        'md:hidden': index !== activeImageIndex,
                    }"
                >
                    <!-- Scaled thumbnail placeholder (not shown for first image, only on lg+ screens) -->
                    <MiscImage
                        v-if="index !== 0"
                        :src="image?.media?.url"
                        :alt="image?.media?.alt"
                        :quality="50"
                        width="80"
                        height="108"
                        fit="fit"
                        background="ffffff"
                        :modifiers="{ extend: 1, trim: '1:ffffff:1' }"
                        class="absolute inset-0 p-6 no-forced-colors:mix-blend-multiply w-full h-full object-contain transition-all duration-300 blur-sm hidden md:block"
                        :class="[
                            mainImagesLoaded.has(index)
                                ? 'opacity-0'
                                : 'opacity-100'
                        ]"
                        lazy-load
                    />
                    <!-- Main image -->
                    <NuxtImg
                        v-if="image?.media?.url"
                        :src="image?.media?.url"
                        :alt="image?.media?.alt"
                        width="1192"
                        height="1605"
                        fit="fit"
                        sizes="367px sm:704px md:584px lg:755px xl:926px 2xl:1192px"
                        background="ffffff"
                        :modifiers="{ extend: 1, trim: '1:ffffff:1' }"
                        class="hover:cursor-zoom-in no-forced-colors:mix-blend-multiply w-full h-auto p-6 md:max-w-[546px]"
                        :class="[
                            index === 0
                                ? ''
                                : mainImagesLoaded.has(index)
                                    ? 'md:opacity-100 md:transition-opacity md:duration-300'
                                    : 'md:opacity-0 md:transition-opacity md:duration-300'
                        ]"
                        :preload="index === 0 ? { fetchPriority: 'high' } : false"
                        :fetchpriority="index === 0 ? 'high' : undefined"
                        :loading="index !== 0 ? 'lazy' : 'eager'"
                        @click="openFullScreenGallery(index)"
                        @load="onMainImageLoad(index)"
                    />
                    <img
                        v-else
                        src="/images/placeholder-image.png"
                        alt="Placeholder"
                        data-not-lazy
                        class="w-full h-auto"
                    >
                </div>
                <ProductListingCardBadges :data="product" class="top-6 z-20 left-2.5" :limit-width="true" />
                <ProductDetailPromotionBadge class="left-2.5" />
            </div>
        </div>

        <ClientOnly>
            <Teleport to="#productMediaGallery">
                <LazyProductDetailMediaGalleryFullscreen
                    v-if="initFullScreenGallery && media"
                    ref="fullScreenGallery"
                    :media="media"
                />
            </Teleport>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { useResizeObserver, useMediaQuery } from '@vueuse/core'

const { product, changeVariant } = useProduct()
const { imageUrl: courseframeImageUrl } = useCourseframe()
const media = computed(() => {
    if (courseframeImageUrl.value != null) {
        return [{
            media: { url: courseframeImageUrl.value, alt: product?.value?.name ?? '' }
        }] as Schemas['Product']['media']
    }
    return product?.value?.media
})
const imageGallery: Ref<HTMLElement | null> = ref(null)
const desktopView = ref(false)
const mounted = ref(false)
const fullScreenGallery = ref()
const initFullScreenGallery = ref(false)
const swiperInitTemp: Ref<null | number> = ref(null)

// New state for thumbnail gallery
const activeImageIndex = ref(0)
const thumbnailContainer: Ref<HTMLElement | null> = ref(null)
const thumbnailColumn: Ref<HTMLElement | null> = ref(null)
const mainImageContainer: Ref<HTMLElement | null> = ref(null)
const thumbnailScrollOffset = ref(0)
const thumbnailColumnHeight = ref(0) // Start with 0, only show when calculated
const thumbnailItemHeight = 116 // Height of each thumbnail (100px image + 8px padding + 8px gap)

// Image loading state tracking
const mainImagesLoaded = ref(new Set<number>())

function eventListenerMediaGallery (e: { data: Schemas['Product'] }) {
    changeVariant(e.data)
    // Reset gallery state when variant changes
    activeImageIndex.value = 0
    thumbnailScrollOffset.value = 0
    mainImagesLoaded.value.clear()
}

useResizeObserver(imageGallery, () => {
    setDesktopView()
})

// Watch main image container height and update thumbnail column height
useResizeObserver(mainImageContainer, () => {
    if (mainImageContainer.value?.children) {
        const activeChild = mainImageContainer.value.children[activeImageIndex.value] as HTMLElement
        thumbnailColumnHeight.value = activeChild?.clientHeight || 0
    }
})

onMounted(() => {
    mounted.value = true
    setDesktopView()
})

// Fires when variant selection is complete
useRuntimeHook('productVariantChanged', eventListenerMediaGallery)
// Fires when variant with media data was selected 
useRuntimeHook('productGalleryDataChanged', eventListenerMediaGallery)

// Fires once the lazily-mounted fullscreen gallery becomes available, to open it on
// the image the user originally clicked.
watch(fullScreenGallery, (gallery) => {
    if (gallery && swiperInitTemp.value != null) {
        nextTick(() => {
            gallery.openModal(swiperInitTemp.value)
            swiperInitTemp.value = null
        })
    }
})

const isSmallScreen = useMediaQuery('(max-width: 768px)')

function setDesktopView () {
    desktopView.value = !isSmallScreen.value;
}

function openFullScreenGallery (index: number) {
    if (desktopView.value) {
        if (!initFullScreenGallery.value) {
            // First time - initialize and store the index
            initFullScreenGallery.value = true
            swiperInitTemp.value = index
        } else if (fullScreenGallery?.value) {
            // Component already mounted - open directly
            fullScreenGallery.value.openModal(index)
        } else {
            // Component mounting - store index for the fullScreenGallery watcher
            swiperInitTemp.value = index
        }
    }
}

const visibleThumbnailCount = computed(() => {
    if (thumbnailColumnHeight.value <= 0 || !media.value?.length) return 0
    // Account for arrow heights: h-8 + mb-2 + mt-2 = 32px + 8px = 40px per arrow
    const arrowSpace = media.value.length > 1 ? 80 : 0 // Space for both arrows
    const availableHeight = Math.max(0, thumbnailColumnHeight.value - arrowSpace)
    return Math.floor(availableHeight / thumbnailItemHeight)
})

const canScrollUp = computed(() => {
    return thumbnailScrollOffset.value > 0 && (media.value?.length || 0) > 1
})

const canScrollDown = computed(() => {
    if (!media.value?.length || media.value.length <= 1 || visibleThumbnailCount.value <= 0) {
        return false
    }
    // Can scroll down if there are more items than visible and we haven't reached max scroll
    const maxScrollOffset = Math.max(0, (media.value.length - visibleThumbnailCount.value) * thumbnailItemHeight)
    return media.value.length > visibleThumbnailCount.value && thumbnailScrollOffset.value < maxScrollOffset
})

// New functions for thumbnail gallery
function setActiveImage(index: number) {
    activeImageIndex.value = index
}

function scrollThumbnailUp() {
    if (canScrollUp.value) {
        thumbnailScrollOffset.value = Math.max(0, thumbnailScrollOffset.value - thumbnailItemHeight)
    }
}

function scrollThumbnailDown() {
    if (canScrollDown.value && media.value?.length) {
        // Ensure we can show complete items, including the last one
        const maxScrollOffset = Math.max(0, (media.value.length - visibleThumbnailCount.value) * thumbnailItemHeight)
        thumbnailScrollOffset.value = Math.min(maxScrollOffset, thumbnailScrollOffset.value + thumbnailItemHeight)
    }
}

function onMainImageLoad(index: number) {
    // Only track loading state for non-first images
    if (index !== 0) {
        mainImagesLoaded.value.add(index)
    }
}
</script>
